export type AccountWorkspaceSection = 'overview' | 'services' | 'tickets' | 'billing';

interface AccountRouteMatch {
  accountId: string | null;
  section: AccountWorkspaceSection;
  ticketId: string | null;
}

export function parseAccountRoute(path: string): AccountRouteMatch {
  const cleanPath = normalizePath(path);
  const match = cleanPath.match(/^\/accounts\/([^/]+?)(?:\/(services|tickets|billing))?(?:\/([^/]+))?$/);

  if (!match) {
    return {
      accountId: null,
      section: 'overview',
      ticketId: null
    };
  }

  const [, rawAccountId, rawSection, rawTail] = match;
  return {
    accountId: rawAccountId ? decodeURIComponent(rawAccountId) : null,
    section: (rawSection as AccountWorkspaceSection | undefined) ?? 'overview',
    ticketId: rawSection === 'tickets' && rawTail ? decodeURIComponent(rawTail) : null
  };
}

export function buildAccountRoute(accountId: string, section: AccountWorkspaceSection = 'overview'): string {
  const encodedAccountId = encodeURIComponent(accountId);
  if (section === 'overview') {
    return `/accounts/${encodedAccountId}`;
  }
  return `/accounts/${encodedAccountId}/${section}`;
}

export function buildAccountTicketRoute(accountId: string, ticketId: string | number): string {
  return `${buildAccountRoute(accountId, 'tickets')}/${encodeURIComponent(String(ticketId))}`;
}

export function resolvePortalHomeRoute(_fallbackAccountId?: string | null): string {
  return '/dashboard';
}

export function resolveLegacyAccountRoute(path: string, fallbackAccountId?: string | null): string {
  const cleanPath = normalizePath(path);

  if (cleanPath === '/account') {
    return resolvePortalHomeRoute(fallbackAccountId);
  }

  if (!fallbackAccountId) {
    return resolvePortalHomeRoute(fallbackAccountId);
  }

  if (cleanPath === '/account/services') {
    return buildAccountRoute(fallbackAccountId, 'services');
  }
  if (cleanPath === '/account/tickets' || cleanPath.startsWith('/account/tickets/')) {
    return '/support';
  }
  if (cleanPath === '/account/billing') {
    return buildAccountRoute(fallbackAccountId, 'billing');
  }

  return resolvePortalHomeRoute(fallbackAccountId);
}

export function resolveAccountActionRoute(
  currentPath: string,
  fallbackAccountId: string | null | undefined,
  section: Exclude<AccountWorkspaceSection, 'overview'>
): string {
  const match = parseAccountRoute(currentPath);
  if (match.accountId) {
    return buildAccountRoute(match.accountId, section);
  }
  if (fallbackAccountId) {
    return buildAccountRoute(fallbackAccountId, section);
  }
  return resolvePortalHomeRoute(fallbackAccountId);
}

function normalizePath(path: string): string {
  const raw = (path.split('?')[0] || '/').trim();
  if (!raw || raw === '/') {
    return '/';
  }
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}
